import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { initializeDB } from '../../../config/db';
import { JOB_STORE } from '../../../config/constants';
import { generateUUID } from '../../../utils/uuid';
import { liftEvaluationService } from '../../../services/liftEvaluationService';
import { buildCopy, calcTotalWeight, formatWeight, parseNumber } from './liftEvaluationSchema';

/** Сколько подъёмов показываем сразу и на сколько увеличиваем по кнопке. */
const PAGE_SIZE = 10;
const PAGE_STEP = 15;

/**
 * «Copy to next day» — новый подъём на основе уже отправленного.
 *
 * Отправленная форма живёт на сервере, поэтому источник тянем оттуда (нужен интернет).
 * Список ограничен: подгружаем порциями и даём поиск, иначе на длинном джобе
 * в нём не найтись. Дата подъёма при этом любая — «следующий день» только подставлен.
 *
 * Веса груза и такелажа человек обязан подтвердить или поправить: это единственное,
 * что меняется от смены к смене чаще всего, а под суммой стоят три подписи.
 * Подписи и фотографии в копию не переносятся — они относятся к тому подъёму.
 */
const CopyPreviousLiftDialog = ({ open, onClose, onCreated, isOnline }) => {
    const [jobs, setJobs] = useState([]);
    const [job, setJob] = useState(null);

    const [recent, setRecent] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [limit, setLimit] = useState(PAGE_SIZE);
    const [search, setSearch] = useState('');
    const [loadingRecent, setLoadingRecent] = useState(false);

    const [source, setSource] = useState(null);
    const [loadingSource, setLoadingSource] = useState(false);
    const [objectWeight, setObjectWeight] = useState('');
    const [riggingWeight, setRiggingWeight] = useState('');
    const [liftDate, setLiftDate] = useState(dayjs().add(1, 'day').format('YYYY-MM-DD'));
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (!open) return;
        resetAll();
        loadJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const resetAll = () => {
        setJob(null);
        setRecent([]);
        setHasMore(false);
        setLimit(PAGE_SIZE);
        setSearch('');
        setSource(null);
        setObjectWeight('');
        setRiggingWeight('');
        setLiftDate(dayjs().add(1, 'day').format('YYYY-MM-DD'));
    };

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(JOB_STORE, 'readonly');
            const all = await tx.objectStore(JOB_STORE).getAll();
            setJobs((all || []).sort((a, b) => (b.Job || '').localeCompare(a.Job || '')));
        } catch (error) {
            console.error('Error loading jobs:', error);
        }
    };

    const loadRecent = async (jobCode, nextLimit) => {
        setLoadingRecent(true);
        try {
            const { items, hasMore: more } = await liftEvaluationService.fetchRecentByJob(jobCode, nextLimit);
            setRecent(items);
            setHasMore(more);
            setLimit(nextLimit);
        } catch (error) {
            Swal.fire({
                icon: 'info',
                title: 'Cannot reach the server',
                text: 'Previous lifts are stored on the server — this needs a connection.',
            });
        } finally {
            setLoadingRecent(false);
        }
    };

    const handleJobChange = async (event, value) => {
        setJob(value);
        setSource(null);
        setRecent([]);
        setHasMore(false);
        setSearch('');
        if (value?.Job) await loadRecent(value.Job, PAGE_SIZE);
    };

    const handlePick = async (item) => {
        setLoadingSource(true);
        try {
            const full = await liftEvaluationService.fetchByUuid(item.uuid);
            if (!full) throw new Error('not found');
            setSource(full);
            setObjectWeight(full.objectWeight ?? '');
            setRiggingWeight(full.riggingWeightEstimate ?? '');
            // По умолчанию — день после того подъёма, а не после сегодняшнего.
            setLiftDate(dayjs(full.liftDate || undefined).add(1, 'day').format('YYYY-MM-DD'));
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load that lift evaluation.' });
        } finally {
            setLoadingSource(false);
        }
    };

    /** Поиск по тому, что человек видит в строке: дата, кран, груз. */
    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) return recent;
        return recent.filter((item) => {
            const haystack = [
                item.liftDate ? dayjs(item.liftDate).format('MM/DD/YYYY') : '',
                item.equipmentCode, item.craneType, item.itemsDescription, item.submittedByName,
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(needle);
        });
    }, [recent, search]);

    const total = calcTotalWeight(objectWeight, riggingWeight);
    const originalTotal = source ? calcTotalWeight(source.objectWeight, source.riggingWeightEstimate) : null;
    const weightsChanged = source !== null && total !== originalTotal;

    const sourceDate = source?.liftDate ? dayjs(source.liftDate) : null;
    const dateShortcuts = [
        { label: 'Next day', value: (sourceDate || dayjs()).add(1, 'day').format('YYYY-MM-DD') },
        { label: 'Today', value: dayjs().format('YYYY-MM-DD') },
        { label: 'Tomorrow', value: dayjs().add(1, 'day').format('YYYY-MM-DD') },
    ];

    const handleCreate = async () => {
        if (!parseNumber(objectWeight) || !parseNumber(riggingWeight)) {
            Swal.fire({
                icon: 'error',
                title: 'Confirm the weights',
                text: 'Both the object weight and the estimated rigging weight are required.',
            });
            return;
        }

        setCreating(true);
        try {
            const draft = buildCopy(source, { uuid: generateUUID(), liftDate });
            draft.objectWeight = String(objectWeight);
            draft.riggingWeightEstimate = String(riggingWeight);
            draft.weightsConfirmed = true;

            await liftEvaluationService.saveToIndexedDB(draft);
            onCreated(draft.uuid);
        } catch (error) {
            console.error('Error creating the copy:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not create the copy.' });
        } finally {
            setCreating(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Copy a previous lift</DialogTitle>
            <DialogContent dividers>
                {!isOnline && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Previous lifts are stored on the server — copying needs a connection.
                    </Alert>
                )}

                <Autocomplete
                    options={jobs}
                    getOptionLabel={(option) => `${option.Job || ''} - ${option.JobName || ''}`}
                    value={job}
                    onChange={handleJobChange}
                    disabled={!isOnline}
                    renderInput={(params) => <TextField {...params} label="Job" size="small" />}
                />

                {loadingRecent && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={22} />
                    </Box>
                )}

                {!loadingRecent && job && recent.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        No submitted lift evaluations for this job yet.
                    </Typography>
                )}

                {!source && recent.length > 0 && (
                    <>
                        {/* На длинном джобе подъёмов много — без поиска в списке не найтись. */}
                        {recent.length > 5 && (
                            <TextField
                                fullWidth size="small" sx={{ mt: 2 }}
                                placeholder="Filter by date, crane or load"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
                            />
                        )}

                        <List dense sx={{ mt: 1, maxHeight: 260, overflowY: 'auto' }}>
                            {visible.map((item) => (
                                <ListItemButton key={item.uuid} onClick={() => handlePick(item)} disabled={loadingSource}>
                                    <ListItemText
                                        primary={`${item.liftDate ? dayjs(item.liftDate).format('MM/DD/YYYY') : '—'} · ${item.equipmentCode || item.craneType || '—'}`}
                                        secondary={`${item.itemsDescription || '—'} · ${formatWeight(item.totalWeight)}`}
                                        secondaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>

                        {visible.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Nothing matches “{search}”.
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Showing {visible.length} of {recent.length}
                                {hasMore ? '+' : ''}
                            </Typography>
                            {hasMore && (
                                <Button size="small" onClick={() => loadRecent(job.Job, limit + PAGE_STEP)} disabled={loadingRecent}>
                                    Load more
                                </Button>
                            )}
                        </Box>
                    </>
                )}

                {source && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                                Copying the lift of {sourceDate ? sourceDate.format('MM/DD/YYYY') : '—'}
                                {source.equipmentCode ? ` · ${source.equipmentCode}` : ''}
                            </Typography>
                            <Button size="small" onClick={() => setSource(null)} sx={{ ml: 'auto' }}>
                                Pick another
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Everything is carried over except the signatures and the set-up photos —
                            those belong to that lift and are collected again.
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" gutterBottom>Confirm the weights</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth size="small" inputMode="decimal"
                                    label="A — Weight of object (lbs)"
                                    value={objectWeight}
                                    onChange={(e) => setObjectWeight(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth size="small" inputMode="decimal"
                                    label="B — Estimated rigging (lbs)"
                                    value={riggingWeight}
                                    onChange={(e) => setRiggingWeight(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth size="small" label="Total (A + B)"
                                    value={total === null ? '' : formatWeight(total)}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                        </Grid>

                        {weightsChanged && (
                            <Chip
                                size="small"
                                color="warning"
                                sx={{ mt: 1 }}
                                label={`Changed from ${formatWeight(originalTotal)}`}
                            />
                        )}

                        {/* Копия не обязана быть «на завтра» — день выбирается любой. */}
                        <TextField
                            fullWidth size="small" type="date" label="Lift date" sx={{ mt: 2 }}
                            value={liftDate}
                            onChange={(e) => setLiftDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            {dateShortcuts.map((shortcut) => (
                                <Chip
                                    key={shortcut.label}
                                    label={shortcut.label}
                                    size="small"
                                    variant={liftDate === shortcut.value ? 'filled' : 'outlined'}
                                    color={liftDate === shortcut.value ? 'primary' : 'default'}
                                    onClick={() => setLiftDate(shortcut.value)}
                                />
                            ))}
                        </Stack>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleCreate} disabled={!source || creating}>
                    {creating ? 'Creating…' : 'Confirm and create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CopyPreviousLiftDialog;
